import { useEffect, useRef, useState } from "react";

// Probe with retries + exponential backoff before flipping to "offline",
// so we don't show "sem conexão" on a single dropped packet. Going back
// online is reported immediately on the first successful probe.
const PROBE_URL = "/favicon.ico";
const MAX_ATTEMPTS = 4; // total tries before declaring offline
const BASE_DELAY = 800; // ms — 800, 1600, 3200, 6400 (capped)
const MAX_DELAY = 6000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function probeOnce(signal?: AbortSignal): Promise<boolean> {
  try {
    await fetch(`${PROBE_URL}?_=${Date.now()}`, {
      method: "HEAD",
      cache: "no-store",
      mode: "no-cors",
      signal,
    });
    return true;
  } catch {
    return false;
  }
}

export function useOnlineStatus() {
  const [online, setOnline] = useState(true);
  const runIdRef = useRef(0);

  useEffect(() => {
    const verify = async () => {
      const myRun = ++runIdRef.current;
      // Trust browser when it says online — but still confirm if it says offline.
      if (typeof navigator !== "undefined" && navigator.onLine) {
        if (await probeOnce()) {
          if (runIdRef.current === myRun) setOnline(true);
          return;
        }
      }

      // Retry with backoff before flipping to offline.
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        if (runIdRef.current !== myRun) return; // newer run superseded us
        const ok = await probeOnce();
        if (runIdRef.current !== myRun) return;
        if (ok) {
          setOnline(true);
          return;
        }
        const delay = Math.min(BASE_DELAY * 2 ** attempt, MAX_DELAY);
        await sleep(delay);
      }

      if (runIdRef.current === myRun) setOnline(false);
    };

    verify();
    const onOnline = () => verify();
    const onOffline = () => verify();
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      runIdRef.current++; // invalidate any in-flight retry loop
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  return online;
}
